import { LoadingButton } from "@mui/lab";
import { DialogTitle } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import { Contact, User } from "@repo/server-api";
import { useNotification } from "@repo/shared/components";
import { useTranslation } from "next-i18next";
import React, { useEffect, useState } from "react";

import { validErrorState } from "@repo/legacy/components/helper/Index";

import { useCreateUserLogin, useUpdateUserLogin } from "../../hooks/UserMutationHooks";
import UserSection from "../sections/UserSection";

export default function DialogLoginUser(
	props: Readonly<{
		open: boolean;
		setOpen: (value: boolean) => void;
		refreshData: () => void;
		user: User | null;
		setUser: (value: User) => void;
		contact: Contact | null;
	}>,
) {
	const { t } = useTranslation();

	const { showNotification } = useNotification();

	const [open, setOpen] = useState(false);
	const [isLoaded, setIsLoaded] = useState<boolean>(false);
	const [user, setUser] = useState<User>({
		contactId: null,
		email: "",
		emailVerified: false,
		enabled: false,
		groups: [],
		id: "",
		username: "",
		firstName: "",
		lastName: "",
	});

	const [errors, setErrors] = useState<{
		user?: Record<string, boolean | object | string>;
	}>({});
	const [sendVerificationEmail, setSendVerificationEmail] = useState<boolean>(false);
	const [saveClicked, setSaveClicked] = useState<boolean>(false);

	const [createUserLogin] = useCreateUserLogin();
	const [updateUserLogin] = useUpdateUserLogin();

	useEffect(() => {
		setOpen(props.open);
	}, [props.open]);

	useEffect(() => {
		if (props.user && !isLoaded && props.user.id !== user.id) {
			setUser({
				...props.user,
			});
			setIsLoaded(true);
		}
	}, [props.user]);

	useEffect(() => {
		if (isLoaded) props.setUser(user);
	}, [user]);

	const resetData = () => {
		setUser({
			contactId: null,
			email: "",
			emailVerified: false,
			enabled: false,
			groups: [],
			id: "",
			username: "",
			firstName: "",
			lastName: "",
		});
	};

	const handleClose = () => {
		resetData();
		props.setOpen(false);
		setSaveClicked(false);
		setIsLoaded(false);
		setErrors({});
	};

	const userRef = React.useRef<{
		validate: () => Promise<Record<string, boolean | object | string>>;
	} | null>(null);

	const validate = async () => {
		const localErrors: Record<string, boolean | object | string> = {};

		localErrors.user = (await userRef.current?.validate()) ?? {};
		setErrors(localErrors);

		return validErrorState(localErrors);
	};

	const handleSubmit = async () => {
		const isValid = await validate();
		if (!isValid) {
			return;
		}

		setSaveClicked(true);

		if (user.id) {
			updateUserLogin(user)
				.then((data) => {
					data.forEach(
						(d: {
							userChangeGroups: {
								errors: { message: string }[];
								boolean: boolean;
							};
							userSetEnabled: {
								errors: { message: string }[];
								boolean: boolean;
							};
						}) => {
							if (d.userChangeGroups) {
								if (d.userChangeGroups.errors) {
									d.userChangeGroups.errors.forEach((err: { message: string }) => {
										showNotification(err.message, "error");
									});
								}
								if (d.userChangeGroups.boolean) {
									showNotification(t("login.groupsChanged"), "success");
									handleClose();
									props.refreshData();
								}
							}
							if (d.userSetEnabled) {
								if (d.userSetEnabled.errors) {
									d.userSetEnabled.errors.forEach((err: { message: string }) => {
										showNotification(err.message, "error");
									});
								}
								if (d.userSetEnabled.boolean) {
									showNotification(t("login.enableChanged"), "success");
									handleClose();
									props.refreshData();
								}
							}
						},
					);
					setSaveClicked(false);
				})
				.catch((error) => {
					showNotification(error.message, "error");
					setSaveClicked(false);
				});
		} else {
			createUserLogin(props.contact, user, sendVerificationEmail)
				.then((data) => {
					const result = data as {
						userAddLogin: {
							user: User | null;
							errors: {
								message: string;
								httpResponse: string;
							}[];
						};
					};
					if (result.userAddLogin.user) {
						props.refreshData();
						handleClose();
						showNotification(t("login.loginCreated"), "success");
					} else if (result.userAddLogin.errors) {
						result.userAddLogin.errors.forEach((err: { message: string; httpResponse: string }) => {
							showNotification(err.message, "error");
							if (err.httpResponse) {
								const res = JSON.parse(err.httpResponse);
								showNotification(res.errorMessage, "error");
							}
						});
					}
					setSaveClicked(false);
				})
				.catch((error) => {
					showNotification(error.message, "error");
					setSaveClicked(false);
				});
		}
	};

	return (
		<Dialog open={open} maxWidth="sm" fullWidth={true}>
			<DialogTitle>{user.id ? t("edit") : t("create-login")}</DialogTitle>
			<DialogContent className="flex-col gap-5">
				<UserSection
					isDialog={true}
					ref={userRef}
					user={user}
					setUser={setUser}
					contact={props.contact}
					errors={errors.user}
					setErrors={(value) => setErrors({ ...errors, user: { ...errors.user, ...value } })}
				/>
				{!user.id && (
					<FormGroup className="padding7 left">
						<FormControlLabel
							control={
								<Switch
									onChange={(event) => {
										setSendVerificationEmail(event.target.checked);
									}}
									checked={sendVerificationEmail}
								/>
							}
							label={t("send-verification-email")}
						/>
					</FormGroup>
				)}
			</DialogContent>
			<DialogActions>
				<Button variant="outlined" onClick={handleClose} autoFocus>
					{t("cancel")}
				</Button>
				<LoadingButton
					loading={saveClicked}
					className={saveClicked ? "" : "blueButton"}
					variant="contained"
					onClick={handleSubmit}>
					{t("save")}
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);
}
